import { useEffect, useState, type JSX } from 'react';
import { CallResult } from '../../../Api/APIService';
import { DetailPanelEntity, DetailPanelParams } from '../DetailPanel/DetailPanel';
import { FinancialEditPayload } from '../DetailPanel/EditModal/financialEditHelpers';
import { DetailPanelContainer } from '../DetailPanel/DetailPanelContainer';
import { DetailViewCard } from './DetailViewCard';

export interface ValidatorResult {
  isValid: boolean;
  messages: { field: string; message: string }[];
  invalidFields: string[];
}

export interface DetailViewValidationCardProps {
  title: string;
  data: DetailPanelParams[];
  saveAction?: (
    data: DetailPanelEntity | FinancialEditPayload
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<CallResult<any>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  detailValidator: (d: any) => ValidatorResult;
  enableCollapse?: boolean;
  cardActions?: JSX.Element | JSX.Element[];
  children?: JSX.Element | JSX.Element[];
  dataTestId?: string;
  wrapperClassName?: string;
}

export function DetailViewValidationCard({
  enableCollapse = false,
  title,
  data,
  children,
  saveAction,
  detailValidator,
  cardActions,
  dataTestId,
  wrapperClassName
}: DetailViewValidationCardProps) {
  const [validationResult, setValidationResult] = useState<ValidatorResult>(
    detailValidator(data.length ? data[0].data : {})
  );

  const handleSave = async (d: DetailPanelEntity | FinancialEditPayload) => {
    if (saveAction) {
      const result = await saveAction(d);
      const validationResult = detailValidator(result?.data);
      setValidationResult(validationResult);
      return result;
    }
    return { data: null, error: null };
  };

  useEffect(() => {
    setValidationResult(detailValidator(data.length ? data[0].data : {}));
  }, [data.length, JSON.stringify(data)]);

  return (
    <DetailViewCard
      cardActions={cardActions}
      enableCollapse={enableCollapse}
      key="commissions_detail"
      className={validationResult?.isValid ? '' : '[&&]:border-[#fa5f16] border-4'}
      titleIcon={validationResult?.isValid ? undefined : 'alert'}
      titleColor={validationResult?.isValid ? 'grey9' : 'orange'}
      title={title}>
      <div className={wrapperClassName || ''}>
        {data.length > 0 && (
          <DetailPanelContainer
            data={data}
            saveAction={handleSave}
            invalidFields={validationResult?.invalidFields}
            dataTestId={dataTestId}
          />
        )}
        {children}
      </div>
    </DetailViewCard>
  );
}
